import {
  ENTITY_CLASS,
  type FacilityKey,
  type FacilityStaffQueryFilters,
  type OrganizationKey,
  type QueryOptions,
  type UserKey,
  isFacilityKey,
  isUserKey,
  streamError,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";

import { fetchData } from "./functions";
import { QueryKey } from "./query";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// 単体取得
// =============================================================

export async function findFacilityStaff(
  facilityKey: FacilityKey | false | null | undefined,
  userKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  if (!facilityKey || !isUserKey(userKey)) {
    return undefined;
  }
  const { output } = await fetchData({
    type: "Entity::FacilityStaff",
    input: { facilityKey, userKey },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function FacilityStaffQueryKey(
  facilityKey: FacilityKey | false | null | undefined,
  userKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return QueryKey(ENTITY_CLASS.FACILITY_STAFF, facilityKey, userKey);
}

/** @deprecated 代わりに`useFacilityStaffQuery`を使用する。 */
export function useFacilityStaff(
  facilityKey: FacilityKey | false | null | undefined,
  userKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
  options?: {
    /** 仮想化された一覧で表示される場合に`true`を設定するフラグ */
    virtualized?: boolean;
  },
) {
  const { data: facilityStaff } = useQuery({
    queryKey: FacilityStaffQueryKey(facilityKey, userKey),
    async queryFn() {
      return findFacilityStaff(facilityKey, userKey);
    },

    enabled: isFacilityKey(facilityKey) && isUserKey(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: !options?.virtualized,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  return facilityStaff;
}

export function useFacilityStaffQuery(
  facilityKey: FacilityKey | false | null | undefined,
  userKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return useQuery({
    queryKey: FacilityStaffQueryKey(facilityKey, userKey),
    async queryFn() {
      return findFacilityStaff(facilityKey, userKey);
    },

    enabled: isFacilityKey(facilityKey) && isUserKey(userKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

// =============================================================
// 一覧
// =============================================================

export async function listFacilityStaffs(
  filters?: FacilityStaffQueryFilters,
  options?: QueryOptions,
) {
  const { output } = await fetchData({
    type: "Entities::FacilityStaffs",
    input: { filters, options },
  });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function FacilityStaffsQueryKey(...[queries, options]: ReadonlyArray<any>) {
  return QueryKey(ENTITY_CLASS.FACILITY_STAFF + "List", queries, options);
}

export function removeFacilityStaffsCache() {
  TANSTACK_QUERY_CLIENT.invalidateQueries({ queryKey: FacilityStaffsQueryKey().slice(0, 1) });
}

export function useListedFacilityStaffs(
  filters?: FacilityStaffQueryFilters,
  options?: QueryOptions & {
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled?: boolean;
  },
) {
  const { data, error, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: FacilityStaffsQueryKey(filters, options),
    async queryFn(context) {
      const { facilityStaffs, nextPageToken } = await listFacilityStaffs(filters, {
        ...options,
        nextPageToken: context.pageParam.nextPageToken,
      });

      return { facilityStaffs, nextPageToken };
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: 60 * 1000,
  });

  useEffect(
    function () {
      if (error) {
        streamError(error);
      }
    },
    [error],
  );

  return useMemo(
    function () {
      return {
        error,
        fetchNextPage,
        loaded: !hasNextPage,
        loading: isLoading,
        pages: data?.pages ?? [],
      };
    },
    [data?.pages, error, fetchNextPage, hasNextPage, isLoading],
  );
}
